import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { DataBoxSelectProps } from '@gov-nx/ui/types';
import {
	dataBoxParams,
	useDataBoxShape,
	useEmailShape,
	useIcNumberShape,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { DataRequest, FormData } from './service.types';

export interface FormInstanceProps {
	code: ServiceCode;
	isEmail: boolean;
	email?: string;
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	isLowAuthorization: boolean;
}

export interface Fields {
	databox: DataBoxSelectProps;
}

interface Form {
	getFields: (isProcessing: boolean) => Fields;
	formDefinition: FormDefinition<FormData>;
}

export function FormInstance({
	code,
	isEmail,
	email,
	dataBoxTypes,
	isLowAuthorization,
}: FormInstanceProps): Form {
	const { t } = useLocale(code);
	const { getIcNumberShape } = useIcNumberShape();
	const { getDataBoxShape, getDataBoxDefaultValues, dataBoxesOptions } =
		useDataBoxShape({
			types: dataBoxTypes,
		});
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization,
	});
	const { getEmailShape } = useEmailShape();

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const formSchema: yup.ObjectSchema<FormData> = yup
		.object({
			_registeredLocation: yup.string().optional(),

			...getDataBoxShape({}),

			email: getEmailShape({
				invalidShapeMessage: t(
					'formular.validace.email-neni-ve-spravnem-tvaru'
				),
			}),
			ico: getIcNumberShape({})
				.optional()
				.when('_registeredLocation', {
					is: 'czech',
					then: (schema) => schema.required(t('formular.validace.ic')),
				}),
			nazevSpolecnosti: yup
				.string()
				.optional()
				.when('_registeredLocation', {
					is: 'foreign',
					then: (schema) =>
						schema.required(t('formular.validace.nazev-spolecnosti')),
				}),
			pravniForma: yup
				.string()
				.optional()
				.when('_registeredLocation', {
					is: 'foreign',
					then: (schema) =>
						schema.required(t('formular.validace.pravni-forma')),
				}),
			adresaSidla: yup
				.string()
				.optional()
				.when('_registeredLocation', {
					is: 'foreign',
					then: (schema) =>
						schema.required(t('formular.validace.adresa-sidla')),
				}),
			vypisZeZahranici: yup
				.string()
				.optional()
				.when('_registeredLocation', {
					is: 'foreign',
					then: (schema) =>
						schema.required(t('formular.validace.vypis-ze-zahranici')),
				}),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			...getDataBoxDefaultValues(),
			_registeredLocation: 'czech',
			email: isEmail ? email : undefined,
		},
	});

	return {
		getFields: (isDisabled) => ({
			databox: {
				field: {
					name: 'datovaSchrankaId',
					isDisabled,
					options: dataBoxesOptions,
				},
				label: {
					children: t('formular.pole.datova-schranka'),
				},
			},
		}),
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
}

export const prepareSubmitData =
	(email?: string) =>
	(formData: FormData): DataRequest => ({
		params: {
			...dataBoxParams(formData),
			kontrola: false,
		},
		body: {
			kodPodani: 'REJSTRIK_TRESTU_VYPIS_EGSB_2',
			typOsoby: 'PO',
			email:
				formData._registeredLocation === 'foreign'
					? formData?.email
						? formData.email
						: email
					: undefined,
			...(formData._registeredLocation === 'foreign'
				? {
						nazevSpolecnosti: formData.nazevSpolecnosti,
						pravniForma: formData.pravniForma,
						adresaSidla: formData.adresaSidla,
						vypisZeZahranici: formData.vypisZeZahranici,
					}
				: { ico: formData.ico }),
		},
	});

export function prepareSubmitDataDS(formData: FormData) {
	return {
		params: {
			...dataBoxParams(formData),
		},
		body: {
			typVypisu: 'REJSTRIK_TRESTU_PO',
			data: {
				...(formData._registeredLocation === 'foreign'
					? {
							nazev: formData.nazevSpolecnosti,
							pravniForma: formData.pravniForma,
							sidlo: formData.adresaSidla,
							stat: formData.vypisZeZahranici,
						}
					: { ico: formData.ico }),
			},
		},
	};
}
